// client/src/components/services_tester/ServicesTesterHeader.jsx
import React from 'react';
import {
  Typography,
  Box,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  HelpOutline as UnknownIcon,
  VpnKey,
  Visibility,
  AdminPanelSettings,
  Security,
  Storage,
  CloudQueue,
  AccountTree,
} from '@mui/icons-material';

import PagePanel from '../common/PagePanel';

// ============================================================================
// Хелперы
// ============================================================================

const STATUS_ICON = {
  connected: CheckCircle,
  healthy: CheckCircle,
  success: CheckCircle,
  error: ErrorIcon,
  unhealthy: ErrorIcon,
  warning: ErrorIcon,
};

function statusColor(status) {
  if (!status) return 'default';
  if (['connected', 'healthy', 'success'].includes(status)) return 'success';
  if (['error', 'unhealthy'].includes(status)) return 'error';
  if (status === 'warning') return 'warning';
  return 'default';
}

function statusIconComponent(status) {
  if (!status) return UnknownIcon;
  return STATUS_ICON[status] || UnknownIcon;
}

function compactTooltip(name, status, message) {
  return `${name}: ${status || 'не проверено'}${message ? ` — ${message}` : ''}`;
}

// ============================================================================
// Chip сервиса (инфраструктура)
// ============================================================================

function ServiceChip({ name, icon, iconColor, status, message, loading }) {
  const color = statusColor(status);
  const Icon = statusIconComponent(status);
  const isUnknown = !status;

  return (
    <Tooltip title={compactTooltip(name, status, message)} arrow>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Chip
          icon={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              {icon}
              <Icon
                fontSize="inherit"
                sx={{
                  ml: 0.25,
                  color: isUnknown ? 'action.disabled' : 'inherit',
                }}
              />
            </Box>
          }
          label={name}
          color={color}
          variant={isUnknown ? 'outlined' : color === 'default' ? 'outlined' : 'filled'}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.65rem',
            '& .MuiChip-label': { px: 0.75 },
            '& .MuiChip-icon': { ml: 0.5, mr: -0.25, fontSize: 12 },
            color: iconColor && isUnknown ? iconColor : undefined,
          }}
        />
        {loading && (
          <CircularProgress
            size={10}
            sx={{ position: 'absolute', top: -3, right: -3 }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

// ============================================================================
// Основной компонент
// ============================================================================

export default function ServicesTesterHeader({
  isAuthenticated,
  user,
  healthyServicesCount,
  totalServicesCount,
  accessibleServices,
  authRequiredServices,
  mongodbStatus,
  tarantoolStatus,
  rabbitmqStatus,
  kratosStatus,
  featureTreeStatus,
  infrastructureLoading,
  healthCheckLoading,
  handleRabbitMQDetails,
  handleKratosDetails,
  authLoading,
  handleLogin,
  handleCheckAuth,
  handleLogout,
}) {
  const kratosPublic = kratosStatus?.public;
  const kratosUsers = kratosStatus?.users;

  return (
    <PagePanel sx={{ flexShrink: 0 }}>
      <Box sx={{ py: 1.25, px: 1.5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1.5}
        >
          {/* Левая часть — заголовок */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary.main"
                sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}
              >
                🚀 Мониторинг сервисов
              </Typography>
              {healthCheckLoading && <CircularProgress size={14} />}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
            >
              Микросервисы — напрямую, инфраструктура — через ImCon
            </Typography>
          </Box>

          {/* Правая часть — статус */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 0.5 }}
          >
            {/* Auth */}
            <Tooltip
              title={
                isAuthenticated
                  ? `Аутентифицирован: ${user?.email || 'пользователь'}`
                  : `Требуется вход для ${authRequiredServices} сервисов`
              }
              arrow
            >
              <Chip
                icon={isAuthenticated ? <CheckCircle /> : <VpnKey />}
                label={
                  isAuthenticated
                    ? `${user?.email?.split('@')[0] || 'user'}`
                    : 'Не авторизован'
                }
                color={isAuthenticated ? 'success' : 'warning'}
                variant={isAuthenticated ? 'filled' : 'outlined'}
                size="small"
                onClick={isAuthenticated ? undefined : handleLogin}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  '& .MuiChip-label': { px: 0.75 },
                  '& .MuiChip-icon': { ml: 0.5, mr: -0.25, fontSize: 12 },
                  cursor: isAuthenticated ? 'default' : 'pointer',
                }}
              />
            </Tooltip>

            {/* Healthy count */}
            <Chip
              icon={<CheckCircle />}
              label={`${healthyServicesCount}/${accessibleServices}`}
              color={healthyServicesCount === accessibleServices ? 'success' : 'default'}
              variant="outlined"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                '& .MuiChip-label': { px: 0.75 },
                '& .MuiChip-icon': { ml: 0.5, mr: -0.25, fontSize: 12 },
              }}
            />

            {/* MongoDB */}
            <ServiceChip
              name="MongoDB"
              icon={<Storage sx={{ fontSize: 12, color: '#47A248' }} />}
              iconColor="#47A248"
              status={mongodbStatus?.status}
              message={mongodbStatus?.message}
              loading={infrastructureLoading?.mongodb}
            />

            {/* Tarantool */}
            <ServiceChip
              name="Tarantool"
              icon={<Storage sx={{ fontSize: 12, color: '#0066CC' }} />}
              iconColor="#0066CC"
              status={tarantoolStatus?.status}
              message={tarantoolStatus?.message}
              loading={infrastructureLoading?.tarantool}
            />

            {/* RabbitMQ */}
            <ServiceChip
              name="RabbitMQ"
              icon={<CloudQueue sx={{ fontSize: 12, color: '#FF6600' }} />}
              iconColor="#FF6600"
              status={rabbitmqStatus?.status}
              message={rabbitmqStatus?.message}
              loading={infrastructureLoading?.rabbitmq}
            />

            {/* Kratos */}
            <ServiceChip
              name="Kratos"
              icon={<Security sx={{ fontSize: 12, color: '#8B5CF6' }} />}
              iconColor="#8B5CF6"
              status={kratosPublic?.status}
              message={kratosPublic?.message}
              loading={infrastructureLoading?.kratos}
            />

            {/* FeatureTree (deprecated) */}
            <ServiceChip
              name="FeatureTree"
              icon={<AccountTree sx={{ fontSize: 12, color: '#4CAF50' }} />}
              iconColor="#4CAF50"
              status={featureTreeStatus?.status}
              message={featureTreeStatus?.message}
              loading={infrastructureLoading?.featureTree}
            />

            {/* Detail buttons */}
            {rabbitmqStatus?.status === 'connected' && (
              <Tooltip title="Детали RabbitMQ" arrow>
                <IconButton
                  size="small"
                  onClick={handleRabbitMQDetails}
                  color="primary"
                  sx={{ p: 0.25 }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {kratosPublic?.status === 'healthy' && (
              <Tooltip title="Детали Kratos" arrow>
                <IconButton
                  size="small"
                  onClick={handleKratosDetails}
                  color="primary"
                  sx={{ p: 0.25 }}
                >
                  <AdminPanelSettings fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Auth actions */}
            {!isAuthenticated && (
              <Tooltip title="Войти (Kratos)" arrow>
                <IconButton
                  size="small"
                  onClick={handleLogin}
                  disabled={authLoading}
                  color="warning"
                  sx={{ p: 0.25 }}
                >
                  <VpnKey fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isAuthenticated && (
              <Tooltip title="Проверить сессию" arrow>
                <IconButton
                  size="small"
                  onClick={handleCheckAuth}
                  disabled={authLoading}
                  color="primary"
                  sx={{ p: 0.25 }}
                >
                  <Security fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Optional alert for not authenticated */}
        {!isAuthenticated && (
          <Alert
            severity="warning"
            icon={<VpnKey fontSize="small" />}
            sx={{ mt: 1, py: 0.25, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0 } }}
          >
            {`Требуется аутентификация для ${authRequiredServices} сервисов (Kratos). Нажмите 🔑 справа.`}
          </Alert>
        )}

        {/* Kratos users stats */}
        {kratosUsers?.status === 'success' && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Kratos users: {kratosUsers.active_users || 0} активных / {kratosUsers.total_users || 0} всего
            </Typography>
          </Box>
        )}
      </Box>
    </PagePanel>
  );
}
